import { useMemo, useRef } from "react";
import { CategoryTree } from "src/molecules/CategoryTree";
import {
  CategoryTreeWrapper,
  GridWrapper,
  ListingContainer,
} from "./ProductList.styled";
import { AdditionalCategories } from "../AdditionalCategories";
import { CategoryTreeProvider } from "src/contexts/category-tree-context";
import {
  Configure,
  InstantSearch,
  InstantSearchServerState,
  InstantSearchSSRProvider,
} from "react-instantsearch-core-v7";
import { Promotion } from "./Promotion";
import { Grid } from "./Grid";
import { PLPTypes } from "src/types/PLPType.d";
import { NavigationItems } from "src/types/graphql.d";
import {
  BRAND_DESIGNER_ONLY_FACET,
  BRAND_FACET,
  BRAND_IS_PRIVATE_SALE,
  CATEGORIES_FACET,
  CATEGORY_PAGE_ID_FACET,
  COLOUR_FACET,
  DISCOUNT_FACET,
  GENDERS_FACET,
  IS_DELETED,
  LABELS_TYPE_FACET,
  PATTERN_FACET,
  PRICE_FACET,
  SIZE_FACET,
  STYLE_FACET,
} from "src/utils/algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { PLPContextKeys } from "src/utils/algolia/contextKeys";
import { WEB_SHOP } from "src/utils/algolia/analyticsTags";
import { VirtualRefinementList } from "src/atoms/VirtualRefinementList";
import { SearchClient } from "instantsearch.js";
import { useSegment } from "src/hooks/useSegment";
import { useSession } from "next-auth/client";

type Override<
  Type,
  NewType extends { [key in keyof Type]?: NewType[key] }
> = Omit<Type, keyof NewType> & NewType;

interface ProductListDefaultProps {
  slug: string;
  gender: string;
  pageContentType: Exclude<PLPTypes, "COLLECTIONS_PLP" | "SEARCH_PLP">;
  categoryPageId: string;
  indexName: string;
  searchClient: SearchClient;
  serverState?: InstantSearchServerState;
  excludeFilters?: string[];
  title?: string;
  navigationItems?: NavigationItems | null;
  hitsPerPage?: number;
  collectionId?: undefined;
  hideCategoryTree?: boolean;
  query?: undefined;
}

export type ProductListProps =
  | ProductListDefaultProps
  | Override<
      ProductListDefaultProps,
      {
        pageContentType: "COLLECTIONS_PLP";
        collectionId: string;
        categoryPageId?: undefined;
      }
    >
  | Override<
      ProductListDefaultProps,
      {
        pageContentType: "SEARCH_PLP";
        collectionId?: undefined;
        categoryPageId?: undefined;
        query?: string;
      }
    >;

const excludedPromotionsPLPs: PLPTypes[] = [
  "BRAND_PLP",
  "SEARCH_PLP",
  "COLLECTIONS_PLP",
];
const excludeAdditionalCategoriesPLPs: PLPTypes[] = ["SEARCH_PLP"];

function ProductList({
  indexName,
  searchClient,
  gender,
  slug,
  categoryPageId,
  pageContentType,
  serverState,
  excludeFilters,
  title,
  navigationItems,
  collectionId,
  query,
  hideCategoryTree = false,
  hitsPerPage = 72,
}: ProductListProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [session] = useSession();
  const { user: htUser } = useSegment();

  const filters = useMemo(() => {
    const filters = [
      `${GENDERS_FACET}:${localeFix({
        gender,
      })}`,
      `${IS_DELETED}:false`,
      `${BRAND_IS_PRIVATE_SALE}:false`,
    ];

    if (pageContentType === "NEW_IN") {
      filters.push(`${LABELS_TYPE_FACET}:new`);
    }

    if (pageContentType !== "NEW_IN" && categoryPageId) {
      filters.push(`${CATEGORY_PAGE_ID_FACET}:"${categoryPageId}"`);
    }

    if (pageContentType !== "BRAND_PLP") {
      filters.push(`${BRAND_DESIGNER_ONLY_FACET}:false`);
    }

    if (pageContentType === "COLLECTIONS_PLP" && collectionId) {
      filters.push(`collections:"${collectionId}"`);
    }

    return filters.join(" AND ");
  }, [categoryPageId, gender, pageContentType, collectionId]);

  const userToken = useMemo(
    () => session?.user.member_id || htUser?.anonymousId() || undefined,
    [session?.user, htUser]
  );

  return (
    <CategoryTreeProvider>
      <ListingContainer>
        {!hideCategoryTree ? (
          <CategoryTreeWrapper>
            {!excludeAdditionalCategoriesPLPs.includes(pageContentType) ? (
              <AdditionalCategories navigationItems={navigationItems} />
            ) : null}

            <CategoryTree
              gender={gender}
              categoryPageId={categoryPageId}
              pageContentType={pageContentType}
              brandName={title}
              gridRef={gridRef}
            />
          </CategoryTreeWrapper>
        ) : null}

        <GridWrapper
          ref={gridRef}
          className={hideCategoryTree ? "hideCategoryTree" : ""}
        >
          {!excludedPromotionsPLPs.includes(pageContentType) ? (
            <Promotion slug={slug} />
          ) : null}
          <InstantSearchSSRProvider key={null} initialResults={{}}>
            <InstantSearch
              searchClient={searchClient}
              future={{ preserveSharedStateOnUnmount: true }}
              indexName={indexName}
            >
              {[
                CATEGORIES_FACET,
                BRAND_FACET,
                SIZE_FACET,
                PRICE_FACET,
                COLOUR_FACET,
                PATTERN_FACET,
                STYLE_FACET,
                DISCOUNT_FACET,
              ].map((facet) => (
                <VirtualRefinementList key={facet} attribute={facet} />
              ))}
              <Configure
                hitsPerPage={72}
                filters={filters}
                query={query || undefined}
                disjunctiveFacets={[
                  CATEGORY_PAGE_ID_FACET,
                  CATEGORIES_FACET,
                  BRAND_FACET,
                  SIZE_FACET,
                  PRICE_FACET,
                  COLOUR_FACET,
                  PATTERN_FACET,
                  STYLE_FACET,
                  DISCOUNT_FACET,
                ]}
                maxValuesPerFacet={1000}
                getRankingInfo
                ruleContexts={[WEB_SHOP, PLPContextKeys[pageContentType]]}
                analyticsTags={[WEB_SHOP, localeFix({ gender })]}
                userToken={userToken}
                clickAnalytics
              />
              <Grid
                title={title}
                indexName={indexName}
                gender={gender}
                pageContentType={pageContentType}
                slug={slug}
                categoryPageId={categoryPageId}
                excludeFilters={excludeFilters}
                hitsPerPage={hitsPerPage}
                serverState={serverState}
                gridRef={gridRef}
                hideCategoryTree={hideCategoryTree}
              />
            </InstantSearch>
          </InstantSearchSSRProvider>
        </GridWrapper>
      </ListingContainer>
    </CategoryTreeProvider>
  );
}

export { ProductList };
